<template>
  <div
    class="code-step"
    :class="{ 'error-state': props.errorCount >= codeLength }"
  >
    <div>
      <BaseFigureForIcon
        size="medium"
        color="grey-secondary"
        icon="arrow"
        position="left"
        is-button
        @click="$emit('back')"
      />
      <h2 class="h2 color-text-primary code-step__title">
        {{ t("auth.code.title") }}
      </h2>
      <p class="color-grey-blue caption-medium">
        {{ t("auth.code.sentToNumber") }}<br />{{ props.phone }}
      </p>
      <BaseInput
        v-model="code"
        type="code"
        :code-length="codeLength"
        :class="{ 'error-state': props.errorCount > 0 }"
        class="code-step__input"
        @click="handleClick"
      />
      <p
        v-if="props.errorCount > 0 && props.errorCount < 5"
        class="color-error"
      >
        {{ t("auth.code.invalid") }}
      </p>
      <p v-if="props.errorCount >= 5" class="color-error">
        {{
          t("auth.code.tooManyAttempts", {
            count: props.errorCount,
            time: formattedTime,
          })
        }}
      </p>
    </div>
    <button
      class="caption-small--bold code-step__resend color-text-primary pointer"
      :class="{
        'resend-button--disabled': !canResend,
      }"
      :disabled="!canResend"
      @click="handleResend"
    >
      {{ t("auth.code.resend") }}
      <span v-if="!canResend" class="code-step__timer">{{
        formattedTime
      }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, defineEmits, defineProps, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import BaseInput from "@/components/base/BaseInput.vue";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";

const props = defineProps<{
  phone: string;
  errorCount: number;
}>();

const emit = defineEmits(["back", "submit", "resend"]);
const { t } = useI18n();

const codeLength = ref(4);
const code = ref("");
const timer = ref(60);

const canResend = computed(() => timer.value === 0);
const formattedTime = computed(() => {
  const minutes = Math.floor(timer.value / 60);
  const seconds = timer.value % 60;

  return `(${minutes}:${seconds.toString().padStart(2, "0")})`;
});

let timerInterval: number;

const handleSubmit = () => {
  if (code.value.length !== codeLength.value) return;

  emit("submit", code.value);
};

const handleResend = () => {
  emit("resend");
  timer.value = 60;
  startTimer();
};

const startTimer = () => {
  clearInterval(timerInterval);
  timerInterval = setInterval(() => {
    timer.value--;

    if (timer.value <= 0) {
      clearInterval(timerInterval);
    }
  }, 1000);
};

const handleClick = () => {
  if (code.value.length === codeLength.value) {
    code.value = "";
  }
};

onMounted(() => {
  startTimer();
});

watch(
  () => code.value,
  () => {
    if (code.value.length === codeLength.value) {
      handleSubmit();
    }
  }
);
</script>

<style scoped lang="scss">
@import "@/assets/style/main";

.code-step {
  background: var(--color-white);
  padding: 25px 24px 28px 24px;
  border-radius: 32px;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: 100%;

  @media (max-width: 650px) {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  &__title {
    margin: 24px 0;
  }

  &__input {
    margin-top: 32px;
    margin-bottom: 16px;
  }

  &__resend {
    text-align: center;
    margin: 25px 0 20px;
    justify-content: center;
    background: none;
    display: flex;
    align-items: center;
    text-transform: uppercase;
    border: none;

    &.resend-button--disabled {
      color: var(--color-grey-light) !important;
      cursor: not-allowed;
    }
  }

  &__timer {
    margin-left: 5px;
    display: flex;
    align-items: center;
  }
}

.resend-button {
  background: none;
  border: none;
  color: var(--color-primary);
  cursor: pointer;
  font-size: 14px;
  font-family: inherit;
  font-weight: 500;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }

  &--disabled,
  &:disabled {
    cursor: not-allowed;
    text-decoration: none;

    &:hover {
      text-decoration: none;
    }
  }
}

.error-state :deep(.code-input__box) {
  border-color: var(--color-danger-primary);
  color: var(--color-danger-primary);
}
</style>
